import React from 'react';
import {
  Grid,
  Box,
  ListItemText,
  OutlinedInput,
  FormControl,
  InputLabel,
} from '@mui/material';
import SearchableSelect from 'components/Forms/searchable/SearchableSelect';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CustomTextInput from 'components/Forms/CustomTextInput';
import LabelRequired from 'components/FormFields/LabelRequired';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CustomMobilePhone from 'components/MobilePhone/CustomMobilePhone';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useWatch } from 'react-hook-form';
import SearchableBusinessSelect from './SearchableBusinessSelect';

const JobSelectField = ({
  index,
  field,
  getFilteredJobs,
  handleChangeJob,
  formMethods,
  t,
}) => {
  const selectedJobId = useWatch({
    control: formMethods?.control,
    name: `businesses[${index}].jobs[0]._id`,
  });

  return (
    <FormControl key={field.businessUuid} sx={{ width: '100%' }}>
      <InputLabel id={`job-select-label-1`} shrink>
        {t('Job')}
      </InputLabel>
      <SearchableSelect
        labelId={`job-select-label-1`}
        id={`job-select-${field.businessUuid}`}
        value={String(selectedJobId || '')}
        onChange={(event) => handleChangeJob(event, index)}
        input={<OutlinedInput label={t('Job')} size="small" />}
        displayEmpty
        renderValue={(selected) => {
          if (!selected)
            return (
              <span style={{ color: 'rgba(0,0,0,0.6)' }}>
                {t('Select Job')}
              </span>
            );
          const selectedStr = String(selected);
          const job = getFilteredJobs(field.businessUuid)?.find(
            (j) => String(j?._id) === selectedStr
          );
          return job?.title || '';
        }}
        sx={{ '& .MuiInputBase-root': { height: '54px' } }}
      >
        {getFilteredJobs(field.businessUuid)?.map((job) => (
          <MenuItem
            key={job._id}
            value={String(job._id)}
            onClick={() => {
              const current = String(selectedJobId || '');
              const next = current === String(job._id) ? '' : String(job._id);
              handleChangeJob({ target: { value: next } }, index);
            }}
          >
            <ListItemText primary={job.title} />
          </MenuItem>
        ))}
      </SearchableSelect>
    </FormControl>
  );
};
import {
  PADDING_MODAL_ACTION_LEFT,
  PADDING_MODAL_ACTION_RIGHT,
} from 'pages/TableOrdering/config';

const CreateOrEditForm = ({
  visiblePinCode,
  showPinCode,
  jobs = [],
  businesses = [],
  handleAddNewJob,
  handleRemove,
  handleChange,
  handleChangeBusinesses,
  businessesFields,
  businessAvailableJobs,
  formMethods,
}) => {
  const { t } = useTranslation();
  const selectedWatcher = formMethods?.watch;

  const getFilteredJobs = (businessUuid) => {
    if (!businessUuid) return [];
    const entry = businessAvailableJobs?.find((item) =>
      Object.keys(item).includes(businessUuid)
    );
    return entry ? entry[businessUuid] : [];
  };

  const handleChangeJob = (event, businessIndex) => {
    handleChange(event, businessIndex);
  };

  const getBusinessName = (businessUuid) => {
    console.log(businessUuid, '>businessUuid');
    return (
      businesses?.find((item) => item?.uuid === businessUuid)?.companyName || ''
    );
  };

  return (
    <Grid sx={{ mt: 1 }}>
      <Grid container spacing={2}>
        <Grid item md={6} xs={6} lg={6} xl={6}>
          <Box sx={PADDING_MODAL_ACTION_LEFT}>
            <CustomTextInput
              sx={{ width: '100%', height: '64px' }}
              name="firstname"
              label={<LabelRequired> {t('First Name')}</LabelRequired>}
            />
          </Box>
        </Grid>
        <Grid item md={6} xs={6} lg={6} xl={6}>
          <Box sx={PADDING_MODAL_ACTION_RIGHT}>
            <CustomTextInput
              sx={{ width: '100%', height: '64px' }}
              name="lastname"
              label={<LabelRequired> {t('Last Name')}</LabelRequired>}
            />
          </Box>
        </Grid>
        <Grid item md={6} xs={6} lg={6} xl={6}>
          <Box sx={PADDING_MODAL_ACTION_LEFT}>
            <CustomMobilePhone
              sx={{ width: '100%', height: '54px' }}
              name="phone"
              label={t('Phone')}
            />
          </Box>
        </Grid>
        <Grid item md={6} xs={6} lg={6} xl={6}>
          <Box sx={PADDING_MODAL_ACTION_RIGHT}>
            <CustomTextInput
              sx={{ width: '100%', height: '54px' }}
              name="email"
              label={t('Email Address')}
            />
          </Box>
        </Grid>
        <Grid item md={12} xs={12} lg={12} xl={12}>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        <Grid item md={6} xs={6} lg={6} xl={6}>
          <Box sx={PADDING_MODAL_ACTION_LEFT}>
            <CustomTextInput
              sx={{
                width: '100%',
                height: '84px',
                '.MuiFormHelperText-root': { width: '100%', ml: '1px' },
              }}
              name="pinCode"
              helperText={
                'This is the 4 digit numeric code that your employee will enter to log into your system'
              }
              InputProps={{
                type: showPinCode ? 'text' : 'password',
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={visiblePinCode}
                      edge="end"
                    >
                      {showPinCode ? (
                        <VisibilityOff sx={{ fontSize: '1.2rem' }} />
                      ) : (
                        <Visibility sx={{ fontSize: '1.2rem' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              label={<LabelRequired> {t('Pin Code')}</LabelRequired>}
            />
          </Box>
        </Grid>
        <Grid item md={12} xs={12} lg={12} xl={12}>
          <Divider sx={{ mb: 2 }} />
        </Grid>
        {businessesFields?.map((field, index) => {
          if (
            field.businessUuid?.length > 0 &&
            !getBusinessName(field.businessUuid)
          ) {
            return null;
          }

          return (
            <React.Fragment key={field.businessUuid || index}>
              <Grid key={field.businessUuid} item md={6} xs={6} lg={6} xl={6}>
                <Box sx={PADDING_MODAL_ACTION_LEFT}>
                  <SearchableBusinessSelect
                    businesses={businesses}
                    businessesFields={businessesFields}
                    index={index}
                    field={field}
                    handleChangeBusinesses={handleChangeBusinesses}
                    formMethods={formMethods}
                    disabled={index === 0}
                  />
                </Box>
              </Grid>
              <Grid item md={6} xs={6} lg={6} xl={6}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '5px',
                    width: '100%',
                    alignItems: 'flex-start',
                    ...PADDING_MODAL_ACTION_RIGHT,
                  }}
                >
                  <JobSelectField
                    index={index}
                    field={field}
                    getFilteredJobs={getFilteredJobs}
                    handleChangeJob={handleChangeJob}
                    formMethods={formMethods}
                    t={t}
                  />

                  {index === 0 ? (
                    <IconButton aria-label="delete" onClick={handleAddNewJob}>
                      <AddIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleRemove(index)}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>
    </Grid>
  );
};
export default CreateOrEditForm;
